import Icon from "./Icon"

const TONES = {
  teal: {
    ring: "bg-teal-50 text-teal-600",
    confirm: "bg-teal-600 hover:bg-teal-700",
  },
  amber: {
    ring: "bg-amber-50 text-amber-600",
    confirm: "bg-amber-600 hover:bg-amber-700",
  },
  emerald: {
    ring: "bg-emerald-50 text-emerald-600",
    confirm: "bg-emerald-600 hover:bg-emerald-700",
  },
  rose: {
    ring: "bg-rose-50 text-rose-600",
    confirm: "bg-rose-600 hover:bg-rose-700",
  },
}

/**
 * Confirmation step for an action that cannot be undone easily — moving money,
 * deleting records. `details` are label/value lines shown above the buttons so
 * the person can check what they are about to do before they agree. An optional
 * `note` is a short green reassurance line above the details, and `iconTone`
 * colours the icon circle apart from the confirm button. `children` adds an
 * input under the details (an amount, a reason); `confirmDisabled` holds the
 * confirm button until that input is valid.
 */
function ConfirmModal({
  open,
  title,
  message,
  details = [],
  note = "",
  icon = "exclamation-triangle-fill",
  tone = "amber",
  iconTone = null,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  children = null,
}) {
  if (!open) return null

  const toneStyles = TONES[tone] || TONES.amber
  const ringStyles = (iconTone && TONES[iconTone]) || toneStyles

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-full w-full max-w-sm overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div className="px-5 pt-5 text-center">
          <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${ringStyles.ring}`}>
            <Icon name={icon} size="1.4rem" />
          </div>
          <h2 className="mt-3 text-base font-black text-slate-900">{title}</h2>
          {message && <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{message}</p>}
        </div>

        {note && (
          <p className="mx-5 mt-4 flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-left text-xs font-medium leading-snug text-emerald-800">
            <Icon name="shield-check" size="0.9rem" className="mt-px shrink-0" />
            <span>{note}</span>
          </p>
        )}

        {details.length > 0 && (
          <dl className="mx-5 mt-4 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50 px-3 py-1">
            {details.map((detail) => (
              <div key={detail.label} className="flex items-baseline justify-between gap-3 py-2">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {detail.label}
                </dt>
                <dd className="min-w-0 truncate text-sm font-bold text-slate-800">{detail.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {children && <div className="mx-5 mt-4 text-left">{children}</div>}

        <div className="flex gap-2 p-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${toneStyles.confirm}`}
          >
            {busy && (
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConfirmModal
